/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * List component for WidgetInquiries
 *
 * @author Fedorov Maxim
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.1
 */

import type { FC } from 'react';


import { useSelector } from 'react-redux';

import { FlexStyled } from '@components/common/Block/Flexbox';
import { ImgStyled } from '@components/common/Block/Img';
import LinkEllipseBlock from '@components/common/LinkBlock/LinkEllipseBlock';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import {
    EGTMWidgetOrderInquiriesTab,
} from '@modules/analytics/GoogleTagManager/GTMOperations/GTMWidgetOrderInquiriesOperations';
import { IGTMPushAnalyticPayload } from '@modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { appHistoryPush } from '@modules/app/appHistory';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { guardComponentType } from '@modules/role/reducer/guardReducer';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { getInquiryStatusIcon } from '@pages/PageHistoryInquiry/utils/getInquiryStatusIcon';
import {
    getPageInquiryInfoUrl,
    isPageInquiryInfoExternalUrl,
} from '@pages/PageInquiryInfo/utils/getInquiryInfoUrl';
import { openExternalRoute } from '@routes/constants/externalRoutes';
import { formatDate, formatInquiries } from '@utils/formatText';

import { WidgetItemLeftContainerStyled, WidgetListItem } from '../../styled/WidgetStyled';
import { WidgetOrderInquiriesListItemDateStyled } from '../styled/widgetOrderInquiriesStyled';
import { IWidgetInquiries } from '../widgetOrderInquiriesTypes';

export interface IWidgetInquiriesList {
    componentsFeature: guardComponentType;
    inquiries: IWidgetInquiries[];
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

export const WidgetInquiriesList: FC<IWidgetInquiriesList> = props => {
    const { componentsFeature, inquiries } = props;
    const isOtherInquiryFeatureEnabled = useSelector(
        makeSelectGuardComponentEnabled(AccessComponentKeyEnum.otherInquiries),
    );

    const onClick = (item: string): void => {
        const url = getPageInquiryInfoUrl(componentsFeature, item);
        if (isPageInquiryInfoExternalUrl(componentsFeature)) {
            openExternalRoute(url);
        } else {
            appHistoryPush(url);
        }
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetOrderInquiries
                .clickLinkOrderOrInquiryWidgetOrderInquiries(item, EGTMWidgetOrderInquiriesTab.inquiries),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        })
    };

    return (
        <>
            {
                inquiries.map((inquiry: IWidgetInquiries) => {
                    const showInquiryLink = !isOtherInquiryFeatureEnabled || !inquiry.other;
                    return (<WidgetListItem key={inquiry.id}>
                                <WidgetItemLeftContainerStyled>
                                    {showInquiryLink ? (<LinkEllipseBlock
                                        className="widget-order-inquiries__item-link"
                                        dataName="redirectToInquiry"
                                        isExternalUrl={true}
                                        title={inquiry.id}
                                        url={getPageInquiryInfoUrl(componentsFeature, inquiry.id.toString())}
                                        onClick={() => onClick(inquiry.id.toString())}
                                    >
                                        {formatInquiries(inquiry.id)}
                                    </LinkEllipseBlock>) : (
                                        <span>
                                            {formatInquiries(inquiry.id)}
                                        </span>
                                    )
                                    }
                                    <WidgetOrderInquiriesListItemDateStyled>
                                        {formatDate(inquiry.date)}
                                    </WidgetOrderInquiriesListItemDateStyled>
                                </WidgetItemLeftContainerStyled>
                                <FlexStyled alignItems="center" justifyContent="flex-end" width="115px">
                                    <ArrowTooltip
                                        dataName="toolTipStatus"
                                        disableHoverListener={false}
                                        title={inquiry.status.name}
                                    >
                                        <ImgStyled
                                            className="small"
                                            src={getInquiryStatusIcon(inquiry.status.id)}
                                        />
                                    </ArrowTooltip>
                                </FlexStyled>
                            </WidgetListItem>)
                    },
                )
            }
        </>
    );
};

export default WidgetInquiriesList;
